import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Toaster, toast } from "sonner";
import UserMenu from "./UserMenu";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { 
  Mail, 
  Server, 
  Send, 
  Download, 
  CheckCircle, 
  XCircle, 
  Loader2,
  ArrowLeft,
  Shield,
  Plus,
  Trash2,
  Users,
  RefreshCw,
  Edit2
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const getAuthHeaders = () => ({
  headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
});

export default function EmailSettings({ user, onLogout }) {
  const navigate = useNavigate();
  
  // Multi-mailbox state
  const [mailboxes, setMailboxes] = useState([]);
  const [users, setUsers] = useState([]);
  const [selectedMailbox, setSelectedMailbox] = useState(null);
  const [showAddDialog, setShowAddDialog] = useState(false);
  
  // Form state for add/edit mailbox
  const [selectedUserId, setSelectedUserId] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [imapServer, setImapServer] = useState("");
  const [imapPort, setImapPort] = useState("993");
  const [imapUseSsl, setImapUseSsl] = useState(true);
  const [smtpServer, setSmtpServer] = useState("");
  const [smtpPort, setSmtpPort] = useState("587");
  const [smtpUseTls, setSmtpUseTls] = useState(true);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  
  // Test email state
  const [testToEmail, setTestToEmail] = useState("");
  const [testSubject, setTestSubject] = useState("ERP System Test Email");
  const [testBody, setTestBody] = useState("This is a test email sent from the ERP system to verify SMTP configuration.");
  
  // Loading states
  const [loading, setLoading] = useState(true);
  const [testingImap, setTestingImap] = useState(false);
  const [testingSmtp, setTestingSmtp] = useState(false);
  const [fetchingEmails, setFetchingEmails] = useState(false);
  const [sendingTest, setSendingTest] = useState(false);
  const [savingSettings, setSavingSettings] = useState(false);
  const [syncingEmails, setSyncingEmails] = useState(false);
  const [deletingMailbox, setDeletingMailbox] = useState(false);
  
  // Results
  const [connectionLogs, setConnectionLogs] = useState([]);
  const [fetchedEmails, setFetchedEmails] = useState([]);
  const [syncResult, setSyncResult] = useState(null);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    await Promise.all([loadMailboxes(), loadUsers()]);
    setLoading(false);
  };

  const loadMailboxes = async () => {
    try {
      const response = await axios.get(`${API}/email/settings/all`, getAuthHeaders());
      setMailboxes(response.data.mailboxes || []);
    } catch (error) {
      if (error.response?.status !== 404) {
        console.error("Failed to load mailboxes:", error);
      }
      setMailboxes([]);
    }
  };

  const loadUsers = async () => {
    try {
      const response = await axios.get(`${API}/users`, getAuthHeaders());
      // Handle both array format and {users: [...]} format
      const usersData = response.data.users || response.data || [];
      setUsers(Array.isArray(usersData) ? usersData : []);
    } catch (error) {
      console.error("Failed to load users:", error);
      setUsers([]);
    }
  };

  const addLog = (message, type = "info") => {
    const timestamp = new Date().toLocaleTimeString("en-IN", { timeZone: "Asia/Kolkata" });
    setConnectionLogs(prev => [...prev, { timestamp, message, type }]);
  };

  const clearLogs = () => {
    setConnectionLogs([]);
    setFetchedEmails([]);
  };

  const resetForm = () => {
    setSelectedUserId("");
    setDisplayName("");
    setImapServer("");
    setImapPort("993");
    setImapUseSsl(true);
    setSmtpServer("");
    setSmtpPort("587");
    setSmtpUseTls(true);
    setEmail("");
    setPassword("");
    setSelectedMailbox(null);
  };

  const openAddDialog = () => {
    resetForm();
    setShowAddDialog(true);
  };

  const openEditDialog = (mailbox) => {
    setSelectedMailbox(mailbox);
    setSelectedUserId(mailbox.user_id || "");
    setDisplayName(mailbox.display_name || "");
    setImapServer(mailbox.imap_server || "");
    setImapPort(String(mailbox.imap_port || "993"));
    setImapUseSsl(mailbox.imap_use_ssl ?? true);
    setSmtpServer(mailbox.smtp_server || "");
    setSmtpPort(String(mailbox.smtp_port || "587"));
    setSmtpUseTls(mailbox.smtp_use_tls ?? true);
    setEmail(mailbox.email || "");
    setPassword(""); // Don't show password
    setShowAddDialog(true);
  };

  const handleSaveMailbox = async (e) => {
    e.preventDefault();
    
    if (!selectedUserId) {
      toast.error("Please select a user for this mailbox");
      return;
    }
    
    // Password only required for NEW mailbox
    if (!password && !selectedMailbox) {
      toast.error("Password is required");
      return;
    }
    
    setSavingSettings(true);
    clearLogs();
    addLog("Saving mailbox settings...", "info");
    
    try {
      const payload = {
        user_id: selectedUserId,
        display_name: displayName || null,
        imap_server: imapServer,
        imap_port: parseInt(imapPort),
        imap_use_ssl: imapUseSsl,
        smtp_server: smtpServer,
        smtp_port: parseInt(smtpPort),
        smtp_use_tls: smtpUseTls,
        email: email
      };
      
      // Only include password if provided (for update, it's optional)
      if (password) {
        payload.password = password;
      }
      
      if (selectedMailbox) {
        // UPDATE existing mailbox using PUT with mailbox ID
        await axios.put(`${API}/email/settings/${selectedMailbox.id}`, payload, getAuthHeaders());
        addLog("Mailbox updated successfully", "success");
        toast.success("Mailbox updated");
      } else {
        // CREATE new mailbox using POST
        payload.password = password; // Password required for new mailbox
        await axios.post(`${API}/email/settings`, payload, getAuthHeaders());
        addLog("Mailbox created successfully", "success");
        toast.success("Mailbox created");
      }
      
      setShowAddDialog(false);
      resetForm();
      await loadMailboxes();
    } catch (error) {
      addLog(`Failed to save: ${error.response?.data?.detail || error.message}`, "error");
      toast.error(error.response?.data?.detail || "Failed to save mailbox");
    } finally {
      setSavingSettings(false);
    }
  };

  const handleDeleteMailbox = async (mailboxId) => {
    if (!confirm("Are you sure you want to delete this mailbox configuration?")) {
      return;
    }
    
    setDeletingMailbox(true);
    try {
      await axios.delete(`${API}/email/settings/${mailboxId}`, getAuthHeaders());
      toast.success("Mailbox deleted");
      await loadMailboxes();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete mailbox");
    } finally {
      setDeletingMailbox(false);
    }
  };

  const handleTestImap = async () => {
    if (!imapServer || !email || !password) {
      toast.error("Please fill in IMAP server, email, and password");
      return;
    }
    
    setTestingImap(true);
    clearLogs();
    addLog("Testing IMAP connection...", "info");
    
    try {
      const response = await axios.post(`${API}/email/test-imap`, {
        server: imapServer,
        port: parseInt(imapPort),
        email: email,
        password: password,
        use_ssl: imapUseSsl
      }, getAuthHeaders());
      
      const { success, message, data } = response.data;
      
      if (data?.logs) {
        data.logs.forEach(log => {
          const type = log.includes("✅") ? "success" : log.includes("❌") ? "error" : "info";
          addLog(log, type);
        });
      }
      
      if (success) {
        toast.success("IMAP connection successful!");
      } else {
        toast.error(message);
      }
    } catch (error) {
      addLog(`Error: ${error.response?.data?.detail || error.message}`, "error");
      toast.error(error.response?.data?.detail || "IMAP test failed");
    } finally {
      setTestingImap(false);
    }
  };

  const handleTestSmtp = async () => {
    if (!smtpServer || !email || !password) {
      toast.error("Please fill in SMTP server, email, and password");
      return;
    }
    
    setTestingSmtp(true);
    clearLogs();
    addLog("Testing SMTP connection...", "info");
    
    try {
      const response = await axios.post(`${API}/email/test-smtp`, {
        server: smtpServer,
        port: parseInt(smtpPort),
        email: email,
        password: password,
        use_tls: smtpUseTls
      }, getAuthHeaders());
      
      const { success, message, data } = response.data;
      
      if (data?.logs) {
        data.logs.forEach(log => {
          const type = log.includes("✅") ? "success" : log.includes("❌") ? "error" : "info";
          addLog(log, type);
        });
      }
      
      if (success) {
        toast.success("SMTP connection successful!");
      } else {
        toast.error(message);
      }
    } catch (error) {
      addLog(`Error: ${error.response?.data?.detail || error.message}`, "error");
      toast.error(error.response?.data?.detail || "SMTP test failed");
    } finally {
      setTestingSmtp(false);
    }
  };

  const handleSyncAllEmails = async () => {
    setSyncingEmails(true);
    clearLogs();
    addLog("Syncing emails from all mailboxes...", "info");
    
    try {
      const response = await axios.post(`${API}/email/sync`, {}, getAuthHeaders());
      
      const { success, synced_count, classified_count, mailboxes_synced, errors } = response.data;
      
      if (success) {
        addLog(`Synced ${synced_count} emails total`, "success");
        addLog(`Classified ${classified_count} emails to projects`, "success");
        
        // Log per-mailbox results
        if (mailboxes_synced) {
          Object.entries(mailboxes_synced).forEach(([mailbox, result]) => {
            addLog(`${mailbox}: ${result.synced} synced, ${result.classified} classified`, "info");
          });
        }
        
        if (errors && errors.length > 0) {
          errors.forEach(err => addLog(`Warning: ${err}`, "warning"));
        }
        
        setSyncResult({ synced: synced_count, classified: classified_count });
        toast.success(`Synced ${synced_count} emails, ${classified_count} classified`);
        await loadMailboxes(); // Refresh to show updated sync times
      } else {
        addLog(`Sync failed`, "error");
        toast.error("Sync failed");
      }
    } catch (error) {
      addLog(`Error: ${error.response?.data?.detail || error.message}`, "error");
      toast.error(error.response?.data?.detail || "Failed to sync emails");
    } finally {
      setSyncingEmails(false);
    }
  };

  // Get users who don't already have a mailbox configured
  const availableUsers = users.filter(u => 
    !mailboxes.some(m => m.user_id === u.id) || 
    (selectedMailbox && selectedMailbox.user_id === u.id)
  );

  // Check if user has email.settings permission (RBAC) or is legacy ADMIN
  const hasEmailSettingsPermission = user?.permissions?.includes("email.settings") || 
                                      user?.role === "ADMIN" ||
                                      user?.role_ids?.includes("role-super-admin");
  
  if (!hasEmailSettingsPermission) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Card className="w-96">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-red-600">
              <Shield className="w-5 h-5" />
              Access Denied
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-gray-600 mb-4">Only administrators can configure email settings.</p>
            <Button onClick={() => navigate("/")} variant="outline" className="w-full">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Dashboard
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div data-testid="email-settings-page">
      <Toaster position="top-right" richColors />
      
      {/* Header */}
      <div className="erp-header">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold">Email Settings</h1>
            <p className="text-blue-100 text-sm mt-1">Configure user mailboxes for project email sync</p>
          </div>
          <div className="flex gap-3">
            <Button
              onClick={() => navigate("/")}
              variant="outline"
              className="bg-white/10 hover:bg-white/20 text-white border-white/30"
              data-testid="back-to-dashboard-btn"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Dashboard
            </Button>
            <UserMenu user={user} onLogout={onLogout} variant="header" />
          </div>
        </div>
      </div>

      <div className="erp-container">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          
          {/* Left Column - Mailboxes List */}
          <div className="lg:col-span-2 space-y-6">
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="flex items-center gap-2">
                      <Users className="w-5 h-5 text-blue-600" />
                      Configured Mailboxes
                    </CardTitle>
                    <CardDescription>
                      Each user can have one mailbox connected for email sync
                    </CardDescription>
                  </div>
                  <div className="flex gap-2">
                    <Button
                      onClick={handleSyncAllEmails}
                      disabled={syncingEmails || mailboxes.length === 0}
                      variant="outline"
                      data-testid="sync-all-btn"
                    >
                      {syncingEmails ? (
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      ) : (
                        <RefreshCw className="w-4 h-4 mr-2" />
                      )}
                      Sync All
                    </Button>
                    <Button
                      onClick={openAddDialog}
                      className="bg-blue-600 hover:bg-blue-700"
                      data-testid="add-mailbox-btn"
                    >
                      <Plus className="w-4 h-4 mr-2" />
                      Add Mailbox
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                {mailboxes.length === 0 ? (
                  <div className="text-center py-8 text-gray-500">
                    <Mail className="w-12 h-12 mx-auto mb-3 opacity-50" />
                    <p>No mailboxes configured yet</p>
                    <p className="text-sm">Click "Add Mailbox" to connect a user's email</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {mailboxes.map((mailbox) => (
                      <div
                        key={mailbox.id}
                        className="p-4 border rounded-lg bg-white hover:bg-gray-50 transition-colors"
                        data-testid={`mailbox-${mailbox.id}`}
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex-1">
                            <div className="flex items-center gap-2">
                              <Mail className="w-4 h-4 text-blue-600" />
                              <span className="font-medium">{mailbox.display_name || mailbox.email}</span>
                              {mailbox.is_active !== false && (
                                <span className="text-xs bg-green-100 text-green-800 px-2 py-0.5 rounded">Active</span>
                              )}
                            </div>
                            <div className="text-sm text-gray-500 mt-1">
                              <span className="mr-4">User: {mailbox.user_name || mailbox.user_email || 'Unknown'}</span>
                              <span>Email: {mailbox.email}</span>
                            </div>
                            <div className="text-xs text-gray-400 mt-1">
                              {mailbox.imap_server}:{mailbox.imap_port}
                              {mailbox.last_sync_at && (
                                <span className="ml-4">
                                  Last sync: {new Date(mailbox.last_sync_at).toLocaleString("en-IN", { timeZone: "Asia/Kolkata" })}
                                </span>
                              )}
                              {mailbox.sync_status && mailbox.sync_status !== 'success' && (
                                <span className="ml-2 text-red-500">{mailbox.sync_status}</span>
                              )}
                            </div>
                          </div>
                          <div className="flex gap-2">
                            <Button
                              onClick={() => openEditDialog(mailbox)}
                              variant="outline"
                              size="sm"
                              data-testid={`edit-mailbox-${mailbox.id}`}
                            >
                              <Edit2 className="w-4 h-4" />
                            </Button>
                            <Button
                              onClick={() => handleDeleteMailbox(mailbox.id)}
                              variant="outline"
                              size="sm"
                              className="text-red-600 hover:bg-red-50"
                              disabled={deletingMailbox}
                              data-testid={`delete-mailbox-${mailbox.id}`}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Sync Results */}
            {syncResult && (
              <Card>
                <CardHeader>
                  <CardTitle className="text-sm">Last Sync Results</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="p-3 bg-green-50 rounded text-sm">
                    <p className="text-green-800">
                      ✅ {syncResult.synced} emails synced, {syncResult.classified} classified to projects
                    </p>
                  </div>
                </CardContent>
              </Card>
            )}
          </div>

          {/* Right Column - Connection Log */}
          <div className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="text-sm">Connection Log</CardTitle>
                <CardDescription>Real-time operation status</CardDescription>
              </CardHeader>
              <CardContent>
                <div 
                  className="bg-slate-900 text-slate-100 rounded-lg p-4 h-64 overflow-y-auto font-mono text-xs"
                  data-testid="connection-log"
                >
                  {connectionLogs.length === 0 ? (
                    <p className="text-slate-500">Logs will appear here...</p>
                  ) : (
                    connectionLogs.map((log, index) => (
                      <div 
                        key={index} 
                        className={`mb-1 ${
                          log.type === "success" ? "text-green-400" :
                          log.type === "error" ? "text-red-400" :
                          log.type === "warning" ? "text-yellow-400" :
                          "text-slate-300"
                        }`}
                      >
                        <span className="text-slate-500">[{log.timestamp}]</span> {log.message}
                      </div>
                    ))
                  )}
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-sm">Quick Info</CardTitle>
              </CardHeader>
              <CardContent className="text-sm text-gray-600 space-y-2">
                <p>• Each user can have one mailbox connected</p>
                <p>• Emails are auto-classified to projects based on Project ID or UUID in email content</p>
                <p>• Sync pulls from INBOX and Sent folders</p>
                <p>• Use "Sync All" to sync all configured mailboxes at once</p>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>

      {/* Add/Edit Mailbox Dialog */}
      <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {selectedMailbox ? "Edit Mailbox" : "Add New Mailbox"}
            </DialogTitle>
            <DialogDescription>
              Configure IMAP/SMTP settings for a user's email account
            </DialogDescription>
          </DialogHeader>
          
          <form onSubmit={handleSaveMailbox} className="space-y-6">
            {/* User Selection */}
            <div className="space-y-2">
              <Label>User *</Label>
              <Select value={selectedUserId} onValueChange={setSelectedUserId}>
                <SelectTrigger data-testid="user-select">
                  <SelectValue placeholder="Select a user" />
                </SelectTrigger>
                <SelectContent>
                  {availableUsers.map((u) => (
                    <SelectItem key={u.id} value={u.id}>
                      {u.name || u.email} ({u.email})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {availableUsers.length === 0 && (
                <p className="text-xs text-amber-600">All users already have mailboxes configured</p>
              )}
            </div>

            {/* Display Name */}
            <div className="space-y-2">
              <Label>Display Name (Optional)</Label>
              <Input
                value={displayName}
                onChange={(e) => setDisplayName(e.target.value)}
                placeholder="e.g., Project Manager Inbox"
                data-testid="display-name-input"
              />
            </div>

            {/* IMAP Settings */}
            <div className="border rounded-lg p-4 space-y-4">
              <h4 className="font-medium flex items-center gap-2">
                <Download className="w-4 h-4 text-blue-600" />
                IMAP Settings (Incoming)
              </h4>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Server *</Label>
                  <Input
                    value={imapServer}
                    onChange={(e) => setImapServer(e.target.value)}
                    placeholder="imap.example.com"
                    required
                    data-testid="imap-server-input"
                  />
                </div>
                <div>
                  <Label>Port *</Label>
                  <Input
                    value={imapPort}
                    onChange={(e) => setImapPort(e.target.value)}
                    placeholder="993"
                    required
                    data-testid="imap-port-input"
                  />
                </div>
              </div>
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="imap-ssl"
                  checked={imapUseSsl}
                  onCheckedChange={setImapUseSsl}
                  data-testid="imap-ssl-checkbox"
                />
                <Label htmlFor="imap-ssl">Use SSL</Label>
              </div>
              <Button
                type="button"
                onClick={handleTestImap}
                disabled={testingImap || !imapServer || !email || !password}
                variant="outline"
                size="sm"
                data-testid="test-imap-btn"
              >
                {testingImap ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Server className="w-4 h-4 mr-2" />}
                Test IMAP
              </Button>
            </div>

            {/* SMTP Settings */}
            <div className="border rounded-lg p-4 space-y-4">
              <h4 className="font-medium flex items-center gap-2">
                <Send className="w-4 h-4 text-green-600" />
                SMTP Settings (Outgoing)
              </h4>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Server *</Label>
                  <Input
                    value={smtpServer}
                    onChange={(e) => setSmtpServer(e.target.value)}
                    placeholder="smtp.example.com"
                    required
                    data-testid="smtp-server-input"
                  />
                </div>
                <div>
                  <Label>Port *</Label>
                  <Input
                    value={smtpPort}
                    onChange={(e) => setSmtpPort(e.target.value)}
                    placeholder="587"
                    required
                    data-testid="smtp-port-input"
                  />
                </div>
              </div>
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="smtp-tls"
                  checked={smtpUseTls}
                  onCheckedChange={setSmtpUseTls}
                  data-testid="smtp-tls-checkbox"
                />
                <Label htmlFor="smtp-tls">Use TLS</Label>
              </div>
              <Button
                type="button"
                onClick={handleTestSmtp}
                disabled={testingSmtp || !smtpServer || !email || !password}
                variant="outline"
                size="sm"
                data-testid="test-smtp-btn"
              >
                {testingSmtp ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Server className="w-4 h-4 mr-2" />}
                Test SMTP
              </Button>
            </div>

            {/* Credentials */}
            <div className="border rounded-lg p-4 space-y-4">
              <h4 className="font-medium flex items-center gap-2">
                <Mail className="w-4 h-4 text-purple-600" />
                Email Credentials
              </h4>
              <div>
                <Label>Email Address *</Label>
                <Input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="user@example.com"
                  required
                  data-testid="email-input"
                />
              </div>
              <div>
                <Label>
                  Password {!selectedMailbox && "*"}
                  {selectedMailbox && <span className="text-xs text-gray-500 ml-2">(leave blank to keep existing)</span>}
                </Label>
                <Input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder={selectedMailbox ? "Leave blank to keep current password" : "Enter password"}
                  required={!selectedMailbox}
                  data-testid="password-input"
                />
              </div>
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowAddDialog(false)}>
                Cancel
              </Button>
              <Button 
                type="submit" 
                disabled={savingSettings || !selectedUserId || !imapServer || !smtpServer || !email}
                className="bg-blue-600 hover:bg-blue-700"
                data-testid="save-mailbox-btn"
              >
                {savingSettings ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <CheckCircle className="w-4 h-4 mr-2" />
                )}
                {selectedMailbox ? "Update Mailbox" : "Add Mailbox"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
